import useCreateAccount from '@/src/Accounts/components/hooks/useCreateAccount';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import useOrgs from './hooks/useOrgs';
import { useEffect, useState } from 'react';
import Loading from '@/src/Common/components/Loading';
import { toast } from 'sonner';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import { Label } from '@/src/shadComponents/ui/label';
import { Card, CardContent } from '@/src/shadComponents/ui/card';
import { Loader2 } from 'lucide-react';
import PageShell from '@/src/Common/components/PageShell';

interface CreateAccountFormValues {
    firstName: string;
    lastName: string;
    username: string;
    email: string;
    password: string;
    orgId: number;
    role: number;
}

export default function OrgAccountCreate() {
    const { orgId } = useParams();
    const initialValues: CreateAccountFormValues = {
        firstName: '',
        lastName: '',
        email: '',
        username: '',
        password: '',
        orgId: orgId ? +orgId : 0,
        role: 1,
    };
    const navigate = useNavigate();
    const { createAccount } = useCreateAccount();

    const { getOrg } = useOrgs();
    const [org, setOrg] = useState<any>({});

    useEffect(() => {
        let isMounted = true;
        const fetchData = async () => {
            try {
                const response = await getOrg(orgId ? +orgId : 0);
                isMounted ?? setOrg(response);
            } catch (error) {
                console.error(error);
            }
        };

        fetchData();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!org) {
        return <Loading isLoading={true} />;
    }

    return (
        <PageShell
            title="Create Account"
            breadcrumbs={[
                { label: 'Admin', href: '/admin/orgs' },
                { label: 'Create Account' },
            ]}
        >
            <Formik
                initialValues={initialValues}
                onSubmit={async (values, actions) => {
                    const response = await createAccount(values);
                    actions.setSubmitting(false);

                    if (response?.data.id) {
                        toast.success('Account created successfully');
                        navigate(`/admin/orgs/${orgId}`);
                    } else {
                        toast.error(`${response.message}`);
                    }
                }}
            >
                {(props) => (
                    <Form
                        className="mt-6"
                        placeholder={undefined}
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                        autoComplete="off"
                    >
                        <input type="hidden" autoComplete="off" />
                        <div className="space-y-12">
                            <div className="grid grid-cols-1 gap-x-8 gap-y-10  pb-12 md:grid-cols-3">
                                <div className="px-8">
                                    <h2 className="text-lg font-semibold tracking-tight text-foreground">
                                        Add a user to {org.name}.
                                    </h2>
                                    <p className="mt-1 text-sm leading-6 text-muted-foreground">
                                        They will be sent an invite to join
                                        after creation.
                                    </p>
                                </div>
                                <Card className="md:col-span-2">
                                    <CardContent className="pt-6 space-y-6">
                                        <div className="space-y-2">
                                            <Label htmlFor="username">Username</Label>
                                            <Field
                                                as={Input}
                                                id="username"
                                                name="username"
                                                autoComplete="off"
                                                autoFill="off"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="email">Email</Label>
                                            <Field
                                                as={Input}
                                                type="email"
                                                id="email"
                                                name="email"
                                                placeholder="yo@email.com"
                                                autoComplete="off"
                                                autoFill="off"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="firstName">First Name</Label>
                                            <Field
                                                as={Input}
                                                id="firstName"
                                                name="firstName"
                                                autoComplete="off"
                                                autoFill="off"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="lastName">Last Name</Label>
                                            <Field
                                                as={Input}
                                                id="lastName"
                                                name="lastName"
                                                autoComplete="off"
                                                autoFill="off"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="password">Password</Label>
                                            <Field
                                                as={Input}
                                                id="password"
                                                type="password"
                                                name="password"
                                                autoComplete="off"
                                                autoFill="off"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="role">Role</Label>
                                            <Field
                                                id="role"
                                                as="select"
                                                name="role"
                                                className="flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                <option value={2}>Client</option>
                                                <option value={1}>User</option>
                                                <option value={0}>Admin</option>
                                            </Field>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        </div>
                        <div className="flex items-center justify-end gap-4 pt-6">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => navigate(-1)}
                            >
                                Cancel
                            </Button>

                            <Button
                                type="submit"
                                disabled={props.isSubmitting}
                            >
                                {props.isSubmitting && <Loader2 className="size-4 animate-spin" />}
                                Create
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </PageShell>
    );
}
